import {
  CheckBadgeIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useGoldenDemands } from '../../hooks/useGoldenDemands';
import { Card } from '../ui/card';
import { Badge } from '../ui/catalyst/badge';
import { Button } from '../ui/catalyst/button';
import { Heading } from '../ui/catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/catalyst/table';
import { Text } from '../ui/catalyst/text';
import { GoldenDemandUploadModal } from './GoldenDemandUploadModal';

export const GoldenDemands = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const { goldenDemands, loading, error, refetch, deleteGoldenDemand } =
    useGoldenDemands();

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        !window.confirm('Are you sure you want to delete this gold standard?')
      ) {
        return;
      }

      try {
        await deleteGoldenDemand(id);
        toast.success('Gold standard deleted successfully');
        refetch();
      } catch (err) {
        toast.error('Failed to delete gold standard');
      }
    },
    [deleteGoldenDemand, refetch],
  );

  const handleUploadComplete = useCallback(() => {
    setIsUploadModalOpen(false);
    refetch();
    toast.success('Gold standard uploaded successfully');
  }, [refetch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Text className="text-red-600">Failed to load gold standards</Text>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Heading level={2}>Gold Standard Demands</Heading>
          <Text className="text-gray-600 mt-1">
            Upload exemplary demand letters as benchmarks for evaluation
          </Text>
        </div>
        <Button
          onClick={() => setIsUploadModalOpen(true)}
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Upload Gold Standard
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm text-gray-600">Total Standards</Text>
              <Text className="text-2xl font-semibold">
                {goldenDemands?.length || 0}
              </Text>
            </div>
            <DocumentIcon className="h-8 w-8 text-blue-500" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm text-gray-600">Processed</Text>
              <Text className="text-2xl font-semibold">
                {goldenDemands?.filter((gs) => gs.processed).length || 0}
              </Text>
            </div>
            <CheckBadgeIcon className="h-8 w-8 text-green-500" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Text className="text-sm text-gray-600">Pending</Text>
              <Text className="text-2xl font-semibold">
                {goldenDemands?.filter((gs) => !gs.processed).length || 0}
              </Text>
            </div>
            <CloudArrowUpIcon className="h-8 w-8 text-orange-500" />
          </div>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Created</TableHeader>
              <TableHeader>Last Modified</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {goldenDemands?.map((goldStandard) => (
              <TableRow key={goldStandard.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <DocumentIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <Text className="font-medium">{goldStandard.name}</Text>
                      {goldStandard.description && (
                        <Text className="text-sm text-gray-600">
                          {goldStandard.description}
                        </Text>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {goldStandard.processed ? (
                    <Badge color="green">Processed</Badge>
                  ) : (
                    <Badge color="orange">Processing</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Text className="text-sm">
                    {new Date(goldStandard.created_at).toLocaleDateString()}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text className="text-sm">
                    {new Date(goldStandard.modified_at).toLocaleDateString()}
                  </Text>
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => handleDelete(goldStandard.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {(!goldenDemands || goldenDemands.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <Text className="text-gray-500">
                    No gold standards uploaded yet
                  </Text>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Upload Modal */}
      <GoldenDemandUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};
